import { useEffect, useRef } from 'react';
import { Property } from '../../../types';
import { MapPin } from '../../../server/properties/propertyTypes';
import { getPropertyByIdApi } from '../../../services/propertyApiClient';
import { createPropertyIcon } from './mapLeafletHelper';

declare var L: any;

interface UseMapClusterLayerProps {
  mapInstance: any;
  markersClusterRef: any;
  pins: MapPin[];
  allFilteredProperties: Property[];
  onSelectProperty: (property: Property) => void;
  onSelectClusterIds: (ids: string[]) => void;
}

export function useMapClusterLayer({
  mapInstance,
  markersClusterRef,
  pins,
  allFilteredProperties,
  onSelectProperty,
  onSelectClusterIds,
}: UseMapClusterLayerProps) {
  const onSelectPropertyRef = useRef(onSelectProperty);
  onSelectPropertyRef.current = onSelectProperty;

  const onSelectClusterIdsRef = useRef(onSelectClusterIds);
  onSelectClusterIdsRef.current = onSelectClusterIds;

  const allFilteredPropertiesRef = useRef(allFilteredProperties);
  allFilteredPropertiesRef.current = allFilteredProperties;

  // 1. Manejo del contenido de pines en el cluster
  useEffect(() => {
    const map = mapInstance.current;
    const cluster = markersClusterRef.current;
    if (!map || !cluster) return;

    const sourcePins =
      pins.length > 0
        ? pins
        : allFilteredPropertiesRef.current.map((p) => ({
            id: p.id,
            codigo: p.codigo,
            lat: p.latitude || 0,
            lng: p.longitude || 0,
            tipoinmueble: p.tipoinmueble,
          }));

    cluster.clearLayers();
    const markers: any[] = [];

    sourcePins.forEach((p: MapPin | any) => {
      if (p.lat && p.lng) {
        const marker = L.marker([p.lat, p.lng], {
          icon: createPropertyIcon(p.tipoinmueble),
          propertyId: p.id,
        });

        marker.on('click', async (e: any) => {
          L.DomEvent.stopPropagation(e);
          map.panTo([p.lat, p.lng]);
          const existing = allFilteredPropertiesRef.current.find((x) => x.id === p.id);
          if (existing) {
            onSelectPropertyRef.current(existing);
          } else {
            const fresh = await getPropertyByIdApi(p.id).catch(() => null);
            if (fresh) onSelectPropertyRef.current(fresh);
          }
        });
        markers.push(marker);
      }
    });

    cluster.addLayers(markers);
  }, [pins, mapInstance, markersClusterRef]);

  // 2. Manejo de selección de cluster para panel lateral (sin bloquear el zoom nativo de Leaflet)
  useEffect(() => {
    const cluster = markersClusterRef.current;
    if (!cluster) return;

    const handleClusterClick = (a: any) => {
      const childMarkers = a.layer.getAllChildMarkers();
      const ids = childMarkers.map((m: any) => m.options?.propertyId).filter(Boolean);
      onSelectClusterIdsRef.current(ids);
    };

    cluster.on('clusterclick', handleClusterClick);
    return () => {
      cluster.off('clusterclick', handleClusterClick);
    };
  }, [markersClusterRef]);
}



